import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Filter, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FileUploadZone from '@/components/documents/FileUploadZone';
import DocumentTable from '@/components/documents/DocumentTable';

export default function DocumentLibrary() {
  const [contextFilter, setContextFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => base44.entities.Document.list()
  });

  const { data: artifacts = [] } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list()
  });

  const { data: rituals = [] } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list()
  });

  const filteredDocuments = documents.filter((doc) => {
    // Archive filter
    if (!showArchived && doc.status === 'archived') return false;
    
    // Context filter
    if (contextFilter !== 'all' && doc.context_type !== contextFilter) return false;
    
    // Company secret filter for Artifact context
    if (doc.context_type === 'Artifact' && doc.parent_context_id) {
      const artifact = artifacts.find(a => a.id === doc.parent_context_id);
      if (artifact?.is_company_secret) {
        const isOwner = artifact.owner_email === user?.email;
        const isEditor = (artifact.editors || []).includes(user?.email);
        const isViewer = (artifact.viewers || []).includes(user?.email);
        if (!isOwner && !isEditor && !isViewer) return false;
      }
    }

    // Company secret filter for Ritual context (if rituals have documents)
    if (doc.context_type === 'Ritual' && doc.parent_context_id) {
      const ritual = rituals.find(r => r.id === doc.parent_context_id);
      if (ritual?.is_company_secret) {
        const isOwner = ritual.owner_email === user?.email;
        const isParticipant = (ritual.participants || []).includes(user?.email);
        if (!isOwner && !isParticipant) return false;
      }
    }
    
    return true;
  });

  const handleUploadComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-indigo-400" />
          <div>
            <h1 className="text-3xl font-bold text-slate-100">Document Library</h1>
            <p className="text-sm text-slate-400 mt-1">Manage all project files stored in Google Drive</p>
          </div>
        </div>
      </div>

      <Card className="glass-panel border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-900 font-semibold tracking-tight leading-none">Upload New Files</CardTitle>
        </CardHeader>
        <CardContent>
          <FileUploadZone
            contextType="General"
            onUploadComplete={handleUploadComplete} />

        </CardContent>
      </Card>

      <Card className="glass-panel border-slate-800">
        <CardHeader className="bg-gray-950 text-slate-400 p-6 flex flex-col space-y-1.5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100">All Documents</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className={`border-slate-700 ${showArchived ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-slate-400'}`}>
                <Archive className="w-4 h-4 mr-2" />
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={contextFilter} onValueChange={setContextFilter}>
                <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contexts</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="Task">Tasks</SelectItem>
                  <SelectItem value="Artifact">Artifacts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ?
          <div className="text-center py-8 text-slate-400">Loading documents...</div> :

          <DocumentTable documents={filteredDocuments} showContext={true} />
          }
        </CardContent>
      </Card>
    </div>);

}